import { rem } from 'polished'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ExploreModalImage from './ExploreModalImage'
import ExploreReviews from './ExploreReviews'
import OpeningHours from './OpeningHours'

import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { ImageCarouselInterstitial } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import Divider from 'LuxKit/Divider'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent, {
  StyledModalContent,
} from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import ExploreBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkButton'
import ExploreBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkToItineraryButton'
import type { ExploreItem } from 'tripPlanner/types/common'
import { cleanUrlString } from 'tripPlanner/utils/explore'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'

/**
 * Since the save/itinerary buttons change content when saving/unsaving the item,
 * we want to avoid them switching between wrapped/unwrapped states. So instead
 * of using ModalFooterButtonBlocks from ModalFooter.tsx, we use our own version
 * which always stacks the buttons vertically at mobile size.
 */
const ModalFooterButtonBlocks = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    flex-direction: row-reverse;
  }
`

const StyledImageCarousel = styled(ImageCarousel)`
  flex-shrink: 0;
  height: ${rem(200)};

  ${mediaQueryUp.tablet} {
    height: ${rem(240)};
  }

  & + ${StyledModalContent} {
    margin-top: ${rem(20)};

    ${mediaQueryUp.tablet} {
      margin-top: ${rem(24)};
    }
  }
`

interface Props {
  item: ExploreItem
  tripId: string
}

function ExploreItemDetails({ item, tripId }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()

  const hasRatingOrReviews = !!(item.rating || item.reviews?.length)

  const subtitleElements = nonNullable([
    item.primaryTypeDisplayName,
    item.place.region,
  ])

  const website = item.website && cleanUrlString(item.website)
  const sep = intl.formatMessage(middleDotSeparatorMessage)

  const interstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    return item.photoNames?.map((name, i) => {
      return {
        id: name,
        position: i,
        element: <ExploreModalImage photoName={name} />,
      }
    })
  }, [item.photoNames])

  return (
    <ModalBase height="full" tabletHeight="auto">
      <ModalHeader
        title={item.name}
        subtitle={subtitleElements.join(` ${sep} `)}
        onCloseButtonClick={() => modalElement.resolve()}
      />
      <ModalBody>
        {item.photoNames && (
          <StyledImageCarousel
            interstitials={interstitials}
            images={EmptyArray}
            eagerLoadFirstImage
            arrowBehaviour="always-visible"
          />
        )}
        <ModalContent>
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={8}>
              {item.place.address && (
                <BodyTextBlock
                  variant="medium"
                  startIcon={<LineMapMarkerIcon />}
                >
                  {item.place.address}
                </BodyTextBlock>
              )}
              {item.phone && (
                <BodyTextBlock variant="medium" startIcon={<LinePhoneIcon />}>
                  {item.phone}
                </BodyTextBlock>
              )}
              {website && (
                <Clickable
                  href={item.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BodyTextBlock
                    variant="medium"
                    startIcon={<LineGlobeIcon />}
                    weight="bold"
                    wrap="word-wrap"
                  >
                    {website}
                  </BodyTextBlock>
                </Clickable>
              )}
              {item.openingHoursWeekdayDescriptions && (
                <OpeningHours
                  descriptions={item.openingHoursWeekdayDescriptions}
                />
              )}
            </VerticalSpacer>

            <BodyText variant="medium">{item.editorialSummary}</BodyText>

            {hasRatingOrReviews && (
              <>
                <Divider kind="primary" />
                <ExploreReviews
                  rating={item.rating}
                  ratingsCount={item.userRatingCount}
                  reviews={item.reviews}
                />
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ModalFooterButtonBlocks>
          <ExploreBookmarkButton
            exploreItem={item}
            variant="text"
            tripId={tripId}
            onSnackbarViewClick={() => modalElement.resolve()}
          />
          <ExploreBookmarkToItineraryButton
            exploreItem={item}
            variant="button"
            tripId={tripId}
            dropdownPlacement="top"
          />
        </ModalFooterButtonBlocks>
      </ModalFooter>
    </ModalBase>
  )
}

export default ExploreItemDetails
